import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../components/layout/TeacherShell';
import { Card, Button, Textarea, DynamicIsland, StickyFooter } from '../../components/ui';
import QuestionTypeSelector from '../../components/questions/QuestionTypeSelector';
import OptionsBuilder from '../../components/questions/OptionsBuilder';
import { cn } from '../../lib/cn';

// Steps are defined inside the component to access t()


const TYPE_LABELS = {
  short_answer: 'Short Answer',
  mcq: 'MCQ',
  essay: 'Essay',
  long_response: 'Long Response',
  fill_in_blank: 'Fill Blank',
  table_sort: 'Table Sort',
};

const BLOOM_LABELS = {
  remember: 'Remember',
  understand: 'Understand',
  apply: 'Apply',
  analyze: 'Analyze',
  evaluate: 'Evaluate',
  create: 'Create',
};

export default function ImportReview({ import: importData, lesson, activity, back_path, has_stimulus_context }) {
  const { t } = useTranslation();

  const ANSWER_GENERATION_STEPS = [
    t('questionImport.readingSource'),
    t('questionImport.matchingQuestions'),
    t('questionImport.draftingAnswers'),
    t('questionImport.citingEvidence'),
    t('questionImport.reviewingQuality'),
  ];

  const [selected, setSelected] = useState(() =>
    new Set((importData.parsed_questions || []).map((_, i) => i))
  );
  const [edits, setEdits] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [generatingAnswers, setGeneratingAnswers] = useState(false);
  const [polling, setPolling] = useState(importData.status === 'pending' || importData.status === 'processing');

  const questions = importData.parsed_questions || [];
  const selectedCount = selected.size;
  const answeredCount = questions.filter(q => q.model_answer || q.correct_answer).length;
  const allAnswered = answeredCount === questions.length;

  // Poll for processing completion
  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(() => {
      router.reload({
        only: ['import'],
        onSuccess: (page) => {
          const status = page.props?.import?.status;
          if (status === 'ready_for_review' || status === 'completed' || status === 'failed') {
            setPolling(false);
          }
        },
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [polling]);

  const toggleSelect = (index) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(questions.map((_, i) => i)));
  const deselectAll = () => setSelected(new Set());

  const updateEdit = (index, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [index]: { ...(prev[index] || {}), [field]: value },
    }));
  };

  const getQuestion = (index) => {
    const base = questions[index];
    const edit = edits[index];
    return edit ? { ...base, ...edit } : base;
  };

  const handleGenerateAnswers = async () => {
    if (!activity) return;
    setGeneratingAnswers(true);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const res = await fetch(
        `/teacher/activities/${activity.id}/question_imports/${importData.id}/generate_answers`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        }
      );
      if (res.ok) {
        const data = await res.json();
        // Apply generated answers as edits so they're included in commit
        if (data.parsed_questions) {
          data.parsed_questions.forEach((q, i) => {
            if (q.model_answer || q.correct_answer) {
              setEdits((prev) => ({
                ...prev,
                [i]: {
                  ...(prev[i] || {}),
                  model_answer: q.model_answer || prev[i]?.model_answer,
                  correct_answer: q.correct_answer || prev[i]?.correct_answer,
                },
              }));
            }
          });
        }
        // Reload to get updated parsed_questions
        router.reload({ only: ['import'] });
      }
    } finally {
      setGeneratingAnswers(false);
    }
  };

  const handleCommit = async () => {
    setSubmitting(true);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const commitUrl = activity
        ? `/teacher/activities/${activity.id}/question_imports/${importData.id}/commit`
        : `/teacher/lessons/${lesson.id}/question_imports/${importData.id}/commit`;
      const fallbackUrl = back_path || (lesson ? `/teacher/lessons/${lesson.id}/question_bank` : '/teacher/dashboard');

      const res = await fetch(commitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          approved_indices: Array.from(selected),
          edits,
        }),
      });
      if (res.redirected) {
        window.location.href = res.url;
      } else if (res.ok) {
        router.visit(fallbackUrl);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Processing state
  if (polling || importData.status === 'pending' || importData.status === 'processing') {
    return (
      <TeacherShell>
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-gray-300 dark:border-gray-600 border-t-gray-900 dark:border-t-white rounded-full animate-spin mb-4" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {importData.status === 'pending' ? t('questionImport.extractingText') : t('questionImport.analyzingQuestions')}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('questionImport.usuallyFewSeconds')}</p>
        </div>
      </TeacherShell>
    );
  }

  // Failed state
  if (importData.status === 'failed') {
    return (
      <TeacherShell>
        <Card className="p-12 text-center">
          <p className="text-sm text-red-500 mb-4">{t('questionImport.importFailed')}</p>
          {importData.processing_errors?.map((err, i) => (
            <p key={i} className="text-xs text-gray-500 dark:text-gray-400">{err.error}</p>
          ))}
          <Button variant="secondary" size="sm" className="mt-4" onClick={() => window.history.back()}>
            {t('questionImport.goBack')}
          </Button>
        </Card>
      </TeacherShell>
    );
  }

  return (
    <TeacherShell scrollTitle={`Review Import · ${activity?.title || lesson?.title || 'Questions'}`}>
      <TeacherPageHeader
        title={t('questionImport.reviewTitle')}
        subtitle={`${questions.length} ${t('questionImport.questionsFound')}${importData.input_type !== 'pasted_text' ? '' : ` ${t('questionImport.fromPastedText')}`}`}
      />

      {/* Dynamic Island for answer generation */}
      <DynamicIsland
        isActive={generatingAnswers}
        steps={ANSWER_GENERATION_STEPS}
        stepDuration={2000}
      />

      {/* Generate Model Answers Banner */}
      {has_stimulus_context && !allAnswered && activity && !generatingAnswers && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
              {answeredCount === 0 ? t('questionImport.noModelAnswers') : `${questions.length - answeredCount} ${t('questionImport.questionsMissingAnswers')}`}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
              {t('questionImport.generateModelAnswers')}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleGenerateAnswers}
          >
            {t('questionImport.generateButton')}
          </Button>
        </div>
      )}

      {/* Question List */}
      <div className="space-y-3 flex-1">
        {questions.map((_, index) => {
          const q = getQuestion(index);
          const isSelected = selected.has(index);
          const isEditing = editingIndex === index;
          const isMcq = q.question_type === 'mcq' || q.question_type === 'multiple_choice';

          return (
            <Card
              key={index}
              className={cn(
                'p-4 transition-all duration-200',
                !isSelected && 'opacity-50'
              )}
            >
              <div className="flex items-start gap-3">
                {/* Number */}
                <span className="flex-shrink-0 w-6 text-xs font-medium text-gray-400 dark:text-gray-500 text-right mt-0.5">
                  {index + 1}
                </span>

                {/* Checkbox */}
                <button
                  onClick={() => toggleSelect(index)}
                  className={cn(
                    'flex-shrink-0 w-5 h-5 rounded border-2 mt-0.5 flex items-center justify-center transition-all duration-200',
                    isSelected
                      ? 'border-gray-900 bg-gray-900 dark:border-white dark:bg-white'
                      : 'border-gray-300 dark:border-gray-600'
                  )}
                >
                  {isSelected && (
                    <svg className="w-3 h-3 text-white dark:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  {/* Meta */}
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                      {TYPE_LABELS[q.question_type] || q.question_type}
                    </span>
                    {q.bloom_level && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {BLOOM_LABELS[q.bloom_level] || q.bloom_level}
                      </span>
                    )}
                  </div>

                  {isEditing ? (
                    /* Edit mode */
                    <div className="space-y-3">
                      <QuestionTypeSelector
                        value={q.question_type}
                        onChange={(type) => updateEdit(index, 'question_type', type)}
                      />
                      <Textarea
                        value={q.prompt}
                        onChange={(e) => updateEdit(index, 'prompt', e.target.value)}
                        rows={3}
                        autoExpand
                      />
                      {(q.question_type === 'mcq' || q.question_type === 'multiple_choice') && q.options && (
                        <OptionsBuilder
                          options={q.options}
                          onChange={(opts) => updateEdit(index, 'options', opts)}
                        />
                      )}
                      <div className="flex justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setEditingIndex(null)}>
                          Done
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <>
                      <p className="text-sm text-gray-900 dark:text-white">{q.prompt}</p>

                      {isMcq && q.options && (
                        <div className="mt-2 space-y-1">
                          {q.options.map((opt, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                              <span className={cn(
                                'w-5 h-5 rounded flex items-center justify-center text-[10px] font-medium',
                                opt.correct
                                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                                  : 'bg-gray-100 dark:bg-gray-700'
                              )}>
                                {String.fromCharCode(65 + i)}
                              </span>
                              <span>{opt.text}</span>
                              {opt.correct && <span className="text-gray-400">✓</span>}
                            </div>
                          ))}
                        </div>
                      )}

                      {q.correct_answer && !isMcq && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('questions.answer')} {q.correct_answer}
                        </p>
                      )}

                      {q.model_answer && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded dark:bg-blue-900/20 dark:border-blue-700">
                          <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-400 uppercase mb-0.5">{t('feedback.modelAnswer')}</p>
                          <p className="text-xs text-blue-900 dark:text-blue-200 whitespace-pre-wrap">{q.model_answer}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Edit button */}
                {!isEditing && (
                  <button
                    onClick={() => setEditingIndex(index)}
                    className="p-1.5 rounded text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                    title="Edit"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <StickyFooter>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={selectAll} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              {t('common.selectAll')}
            </button>
            <button onClick={deselectAll} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              {t('common.deselectAll')}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCommit}
              disabled={selectedCount === 0 || submitting}
            >
              {submitting ? 'Importing...' : `Import ${selectedCount} Selected Question${selectedCount !== 1 ? 's' : ''} →`}
            </Button>
          </div>
        </div>
      </StickyFooter>
    </TeacherShell>
  );
}
